document.documentElement.classList.add('js');

const AyuTheme = {
  lockScroll(lock = true) {
    document.documentElement.classList.toggle('is-locked', lock);
  },

  formatMoney(cents) {
    const currency = document.body?.dataset.currency || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format((cents || 0) / 100);
  },

  emitAnalytics(eventName, detail = {}) {
    const payload = {
      event: eventName,
      page_type: document.body?.dataset.pageType || '',
      timestamp: Date.now(),
      ...detail,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(
      new CustomEvent('ayu:analytics', {
        detail: payload,
      })
    );

    return payload;
  },

  getCartCount() {
    const firstNode = document.querySelector('[data-cart-count]');
    if (!firstNode) return 0;
    const parsed = Number(firstNode.textContent || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  },

  setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0;
    });
  },

  incrementCartCount(delta = 1) {
    this.setCartCount(Math.max(0, this.getCartCount() + delta));
  },

  refreshCartCount() {
    return fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((cart) => {
        this.setCartCount(cart.item_count || 0);
        return cart;
      })
      .catch(() => null);
  },

  openDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    this.lockScroll(true);
  },

  closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.drawer.is-open')) {
      this.lockScroll(false);
    }
  },
};

window.AyuTheme = AyuTheme;

document.addEventListener('click', (event) => {
  const openTrigger = event.target.closest('[data-drawer-open]');
  if (openTrigger) {
    const selector = openTrigger.getAttribute('data-drawer-open');
    AyuTheme.openDrawer(document.querySelector(selector));
  }

  const closeTrigger = event.target.closest('[data-drawer-close], .drawer__scrim');
  if (closeTrigger) {
    AyuTheme.closeDrawer(closeTrigger.closest('.drawer'));
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    const activeDrawer = document.querySelector('.drawer.is-open');
    if (activeDrawer) AyuTheme.closeDrawer(activeDrawer);
  }
});

document.addEventListener('change', (event) => {
  const sortInput = event.target.closest('[data-auto-submit]');
  if (sortInput) {
    AyuTheme.emitAnalytics('sort_change', {
      sort_by: sortInput.value,
      context: sortInput.dataset.sortContext || document.body?.dataset.pageType || 'collection',
    });
    sortInput.form?.submit();
  }
});

document.addEventListener('submit', (event) => {
  const searchForm = event.target.closest('[data-search-form]');
  if (!searchForm) return;

  const query = searchForm.querySelector('input[type="search"]')?.value?.trim() || '';
  AyuTheme.emitAnalytics('search', { query });
});

document.addEventListener('click', (event) => {
  const analyticsTarget = event.target.closest('[data-analytics-click]');
  if (!analyticsTarget) return;

  AyuTheme.emitAnalytics(analyticsTarget.dataset.analyticsClick, {
    item_id: analyticsTarget.dataset.itemId || '',
    item_name: analyticsTarget.dataset.itemName || '',
    item_type: analyticsTarget.dataset.itemType || '',
    list_context: analyticsTarget.dataset.listContext || '',
  });
});

document.addEventListener('click', (event) => {
  const tabButton = event.target.closest('[data-tab-button]');
  if (!tabButton) return;

  const tabRoot = tabButton.closest('[data-collection-tabs]');
  if (!tabRoot) return;

  const tabId = tabButton.dataset.tabId;
  if (!tabId) return;

  tabRoot.querySelectorAll('[data-tab-button]').forEach((button) => {
    const isActive = button === tabButton;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });

  tabRoot.querySelectorAll('[data-tab-panel]').forEach((panel) => {
    panel.hidden = panel.dataset.tabPanel !== tabId;
  });

  AyuTheme.emitAnalytics('collection_tab_change', {
    tab_id: tabId,
    tab_label: tabButton.textContent.trim(),
  });
});

class ProductCardController {
  constructor(root) {
    this.root = root;
    this.productId = root.dataset.productId || '';
    this.productTitle = root.dataset.productTitle || '';
    this.productUrl = root.dataset.productUrl || '';
    this.listContext = root.dataset.listContext || document.body?.dataset.pageType || '';
    this.addButtonLabel = root.dataset.addLabel || 'Add to bag';
    this.buyButtonLabel = root.dataset.buyLabel || 'Buy Now';
    this.soldOutLabel = root.dataset.soldOutLabel || 'Sold Out';
    this.addingLabel = root.dataset.addingLabel || 'Adding…';
    this.processingLabel = root.dataset.processingLabel || 'Processing…';
    this.variants = JSON.parse(root.querySelector('[data-product-card-variants]')?.textContent || '[]');
    this.selectedOptions = JSON.parse(
      root.querySelector('[data-product-card-selected-options]')?.textContent || '[]'
    );
    this.optionSlots = [...root.querySelectorAll('[data-product-card-option-slot]:not(.is-empty)')];
    this.optionButtons = [...root.querySelectorAll('[data-product-card-option-button]')];
    this.optionSelects = [...root.querySelectorAll('[data-product-card-option-select]')];
    this.variantInput = root.querySelector('[data-product-card-variant-input]');
    this.priceCurrent = root.querySelector('[data-product-card-price-current]');
    this.priceCompare = root.querySelector('[data-product-card-price-compare]');
    this.saleBadge = root.querySelector('[data-product-card-sale-badge]');
    this.mediaImage = root.querySelector('[data-product-card-image]');
    this.defaultImageSrc = this.mediaImage?.currentSrc || this.mediaImage?.src || '';
    this.defaultImageAlt = this.mediaImage?.alt || this.productTitle;
    this.linkTargets = [...root.querySelectorAll('[data-product-card-link]')];
    this.form = root.querySelector('[data-product-card-form]');
    this.addButton = root.querySelector('[data-product-card-add]');
    this.addLabel = root.querySelector('[data-product-card-add-label]');
    this.buyButton = root.querySelector('[data-product-card-buy]');
    this.buyLabel = root.querySelector('[data-product-card-buy-label]');
    this.message = root.querySelector('[data-product-card-message]');
    this.isBusy = false;
    this.hasSelectors = this.optionSlots.length > 0;

    this.handleResize = this.handleResize.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRootClick = this.handleRootClick.bind(this);

    this.attachEvents();
    this.syncState(true);
    if (this.hasSelectors) {
      window.addEventListener('resize', this.handleResize);
    }
  }

  attachEvents() {
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled || this.isBusy) return;
        this.selectOption(Number(button.dataset.optionIndex), button.dataset.optionValue || '');
      });
    });

    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        if (this.isBusy) return;
        this.selectOption(Number(select.dataset.optionIndex), select.value);
      });
    });

    this.form?.addEventListener('submit', this.handleSubmit);
    this.buyButton?.addEventListener('click', () => {
      if (this.isBusy) return;
      this.addToCart({ redirectToCheckout: true });
    });
    this.root.addEventListener('click', this.handleRootClick);
  }

  destroy() {
    if (this.hasSelectors) {
      window.removeEventListener('resize', this.handleResize);
    }
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.root.removeEventListener('click', this.handleRootClick);
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.isBusy) return;
    this.addToCart();
  }

  handleRootClick(event) {
    if (event.defaultPrevented) return;
    if (event.target.closest('a, button, select, option, input, label, form')) return;

    const variant = this.getCurrentVariant();
    const href = variant?.id ? `${this.productUrl}?variant=${variant.id}` : this.productUrl;
    if (href) {
      window.location.assign(href);
    }
  }

  handleResize() {
    this.updateSelectorModes();
  }

  getCurrentVariant() {
    return this.variants.find((variant) =>
      variant.options.every((value, index) => value === this.selectedOptions[index])
    );
  }

  getAvailableValues(optionIndex, selections = this.selectedOptions) {
    const exactMatches = this.variants.filter((variant) =>
      variant.options.every((value, index) => {
        if (index === optionIndex) return true;
        const selectedValue = selections[index];
        if (!selectedValue) return true;
        return value === selectedValue;
      })
    );

    return [...new Set(exactMatches.map((variant) => variant.options[optionIndex]).filter(Boolean))];
  }

  normalizeSelections() {
    for (let optionIndex = 0; optionIndex < this.optionSlots.length; optionIndex += 1) {
      const availableValues = this.getAvailableValues(optionIndex, this.selectedOptions);
      if (!availableValues.length) continue;

      if (!this.selectedOptions[optionIndex] || !availableValues.includes(this.selectedOptions[optionIndex])) {
        this.selectedOptions[optionIndex] = availableValues[0];
      }
    }
  }

  selectOption(optionIndex, optionValue) {
    this.selectedOptions[optionIndex] = optionValue;
    this.normalizeSelections();
    this.syncState();

    AyuTheme.emitAnalytics('variant_change', {
      product_id: this.productId,
      product_name: this.productTitle,
      option_index: optionIndex,
      option_value: optionValue,
      list_context: this.listContext,
    });
  }

  updateSelectorModes() {
    this.optionSlots.forEach((slot) => {
      const buttonsWrap = slot.querySelector('[data-product-card-option-buttons]');
      const selectWrap = slot.querySelector('[data-product-card-option-select-wrap]');
      if (!buttonsWrap || !selectWrap) return;

      const preferredMode = slot.dataset.preferredMode || 'buttons';
      let useSelect = preferredMode === 'select';
      if (!useSelect && buttonsWrap.scrollWidth > buttonsWrap.clientWidth + 4) {
        useSelect = true;
      }

      slot.classList.toggle('is-select-mode', useSelect);
      buttonsWrap.hidden = useSelect;
      selectWrap.hidden = !useSelect;
    });
  }

  syncState(initial = false) {
    this.normalizeSelections();
    const availableValuesByIndex = this.optionSlots.map((_, optionIndex) =>
      this.getAvailableValues(optionIndex, this.selectedOptions)
    );

    this.optionButtons.forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      const isSelected = this.selectedOptions[optionIndex] === optionValue;
      const isAvailable = availableValuesByIndex[optionIndex]?.includes(optionValue);

      button.classList.toggle('is-selected', isSelected);
      button.disabled = !isAvailable;
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.optionSelects.forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex);
      [...select.options].forEach((option) => {
        const isAvailable = availableValuesByIndex[optionIndex]?.includes(option.value);
        option.disabled = !isAvailable;
      });
      select.value = this.selectedOptions[optionIndex] || select.value;
    });

    this.updateSelectorModes();

    const variant = this.getCurrentVariant();
    if (variant && this.variantInput) {
      this.variantInput.value = variant.id;
    }

    this.updatePricing(variant);
    this.updateMedia(variant);
    this.updateLinks(variant);
    this.updateActions(variant);

    if (!initial) {
      this.hideMessage();
    }
  }

  updatePricing(variant) {
    if (!variant) return;

    if (this.priceCurrent) {
      this.priceCurrent.textContent = AyuTheme.formatMoney(variant.price);
    }

    if (this.priceCompare) {
      const hasComparePrice = Number(variant.compare_at_price) > Number(variant.price);
      this.priceCompare.hidden = !hasComparePrice;
      if (hasComparePrice) {
        this.priceCompare.textContent = AyuTheme.formatMoney(variant.compare_at_price);
      }
    }

    if (this.saleBadge) {
      const hasDiscount = Number(variant.compare_at_price) > Number(variant.price);
      this.saleBadge.hidden = !hasDiscount;
      if (hasDiscount) {
        const discount = Math.round(
          ((Number(variant.compare_at_price) - Number(variant.price)) / Number(variant.compare_at_price)) * 100
        );
        this.saleBadge.textContent = `${discount}% Off`;
      }
    }
  }

  updateMedia(variant) {
    if (!this.mediaImage) return;

    if (variant?.featured_image) {
      this.mediaImage.src = variant.featured_image;
      this.mediaImage.alt = variant.featured_image_alt || this.productTitle;
      this.mediaImage.removeAttribute('srcset');
      return;
    }

    if (this.defaultImageSrc) {
      this.mediaImage.src = this.defaultImageSrc;
      this.mediaImage.alt = this.defaultImageAlt;
      this.mediaImage.removeAttribute('srcset');
    }
  }

  updateLinks(variant) {
    const href = variant?.id ? `${this.productUrl}?variant=${variant.id}` : this.productUrl;
    this.linkTargets.forEach((link) => {
      link.setAttribute('href', href);
    });
  }

  updateActions(variant) {
    const isAvailable = Boolean(variant?.available);
    const addText = this.isBusy ? this.busyAddText : isAvailable ? this.addButtonLabel : this.soldOutLabel;
    const buyText = this.isBusy ? this.busyBuyText : isAvailable ? this.buyButtonLabel : this.soldOutLabel;

    if (this.addLabel) this.addLabel.textContent = addText;
    if (this.buyLabel) this.buyLabel.textContent = buyText;
    if (this.addButton) this.addButton.disabled = !isAvailable || this.isBusy;
    if (this.buyButton) this.buyButton.disabled = !isAvailable || this.isBusy;
  }

  setBusy(isBusy, addText, buyText) {
    this.isBusy = isBusy;
    this.busyAddText = addText || this.addingLabel;
    this.busyBuyText = buyText || this.processingLabel;
    this.updateActions(this.getCurrentVariant());
  }

  showMessage(text, tone = 'success') {
    if (!this.message) return;
    this.message.hidden = !text;
    this.message.textContent = text || '';
    this.message.dataset.tone = tone;
  }

  hideMessage() {
    if (!this.message) return;
    this.message.hidden = true;
    this.message.textContent = '';
    delete this.message.dataset.tone;
  }

  updateCartCount(count) {
    AyuTheme.setCartCount(count);
  }

  async addToCart({ redirectToCheckout = false } = {}) {
    const variant = this.getCurrentVariant();
    if (!variant?.id || !variant.available) return;

    this.setBusy(true, redirectToCheckout ? 'Adding…' : 'Adding…', redirectToCheckout ? 'Redirecting…' : 'Buy Now');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.description || 'Unable to add this product right now.');
      }

      AyuTheme.incrementCartCount(1);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => AyuTheme.refreshCartCount());
      } else {
        window.setTimeout(() => AyuTheme.refreshCartCount(), 250);
      }

      AyuTheme.emitAnalytics('add_to_cart', {
        product_id: this.productId,
        product_name: this.productTitle,
        variant_id: variant.id,
        list_context: this.listContext,
        source: redirectToCheckout ? 'product_card_buy_now' : 'product_card_add_to_cart',
      });

      if (redirectToCheckout) {
        AyuTheme.emitAnalytics('begin_checkout', {
          product_id: this.productId,
          product_name: this.productTitle,
          variant_id: variant.id,
          list_context: this.listContext,
          source: 'product_card_buy_now',
        });
        window.location.assign('/checkout');
        return;
      }

      this.showMessage('Added to cart', 'success');
    } catch (error) {
      this.showMessage(error.message || 'Unable to add this product right now.', 'error');
    } finally {
      this.setBusy(false);
    }
  }
}

const productCardControllers = new WeakMap();

function initProductCards(container = document) {
  container.querySelectorAll?.('[data-product-card-root]').forEach((root) => {
    if (productCardControllers.has(root)) return;
    productCardControllers.set(root, new ProductCardController(root));
  });
}

function destroyProductCards(container = document) {
  container.querySelectorAll?.('[data-product-card-root]').forEach((root) => {
    const controller = productCardControllers.get(root);
    if (!controller) return;
    controller.destroy();
    productCardControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initProductCards(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initProductCards(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyProductCards(event.target);
});

class SlideshowController {
  constructor(root) {
    this.root = root;
    this.slides = [...root.querySelectorAll('[data-slideshow-slide]')];
    this.dots = [...root.querySelectorAll('[data-slideshow-dot]')];
    this.prevButton = root.querySelector('[data-slideshow-prev]');
    this.nextButton = root.querySelector('[data-slideshow-next]');
    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.interval = Number(root.dataset.interval || 5000);
    this.activeIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );
    this.timer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;

    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    this.attachEvents();
    this.goTo(this.activeIndex, false);
    this.start();
  }

  attachEvents() {
    this.prevButton?.addEventListener('click', this.handlePrev);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
      });
    });
    this.root.addEventListener('mouseenter', this.handleMouseEnter);
    this.root.addEventListener('mouseleave', this.handleMouseLeave);
    this.root.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.root.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  destroy() {
    this.stop();
    this.prevButton?.removeEventListener('click', this.handlePrev);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.root.removeEventListener('mouseenter', this.handleMouseEnter);
    this.root.removeEventListener('mouseleave', this.handleMouseLeave);
    this.root.removeEventListener('touchstart', this.handleTouchStart);
    this.root.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handlePrev() {
    this.goTo(this.activeIndex - 1);
  }

  handleNext() {
    this.goTo(this.activeIndex + 1);
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  }

  handleMouseEnter() {
    this.stop();
  }

  handleMouseLeave() {
    this.start();
  }

  handleTouchStart(event) {
    this.touchStartX = event.changedTouches?.[0]?.clientX || 0;
  }

  handleTouchEnd(event) {
    this.touchEndX = event.changedTouches?.[0]?.clientX || 0;
    const delta = this.touchEndX - this.touchStartX;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) {
      this.handlePrev();
    } else {
      this.handleNext();
    }
  }

  start() {
    if (!this.autoplayEnabled || this.slides.length < 2) return;
    this.stop();
    this.timer = window.setInterval(() => {
      this.goTo(this.activeIndex + 1, false);
    }, this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  goTo(index, restart = true) {
    if (!this.slides.length) return;
    this.activeIndex = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (restart) {
      this.start();
    }
  }

  goToBlock(blockId) {
    const index = this.slides.findIndex((slide) => slide.dataset.slideBlockId === blockId);
    if (index >= 0) {
      this.goTo(index, false);
      this.stop();
    }
  }
}

const slideshowControllers = new WeakMap();

function initSlideshows(container = document) {
  container.querySelectorAll?.('[data-slideshow]').forEach((root) => {
    if (slideshowControllers.has(root)) return;
    slideshowControllers.set(root, new SlideshowController(root));
  });
}

function destroySlideshows(container = document) {
  container.querySelectorAll?.('[data-slideshow]').forEach((root) => {
    const controller = slideshowControllers.get(root);
    if (!controller) return;
    controller.destroy();
    slideshowControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSlideshows(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initSlideshows(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroySlideshows(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('[data-slide-block-id]');
  const slideshowRoot = event.target.closest?.('[data-slideshow]');
  if (!slide || !slideshowRoot) return;
  const controller = slideshowControllers.get(slideshowRoot);
  controller?.goToBlock(slide.dataset.slideBlockId);
});

class CategoryShowcaseController {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-category-track]');
    this.pages = [...root.querySelectorAll('[data-category-page]')];
    this.dots = [...root.querySelectorAll('[data-category-dot]')];
    this.autoplayEnabled = root.dataset.mobileAutoplay === 'true';
    this.loopEnabled = root.dataset.mobileLoop !== 'false';
    this.interval = Number(root.dataset.mobileInterval || 5000);
    this.activeIndex = 0;
    this.timer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;

    this.handleResize = this.handleResize.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    if (!this.track || this.pages.length < 2) {
      return;
    }

    this.attachEvents();
    this.goTo(0, false);
    this.start();
  }

  isMobile() {
    return window.matchMedia('(max-width: 47.99rem)').matches;
  }

  attachEvents() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
      });
    });
    this.root.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.root.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  destroy() {
    this.stop();
    this.root.removeEventListener('touchstart', this.handleTouchStart);
    this.root.removeEventListener('touchend', this.handleTouchEnd);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleResize() {
    if (!this.track) return;
    this.goTo(this.activeIndex, false);
    if (this.isMobile()) {
      this.start();
    } else {
      this.stop();
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  }

  handleTouchStart(event) {
    if (!this.isMobile()) return;
    this.touchStartX = event.changedTouches?.[0]?.clientX || 0;
  }

  handleTouchEnd(event) {
    if (!this.isMobile()) return;
    this.touchEndX = event.changedTouches?.[0]?.clientX || 0;
    const delta = this.touchEndX - this.touchStartX;
    if (Math.abs(delta) < 36) return;
    if (delta > 0) {
      this.goTo(this.activeIndex - 1);
    } else {
      this.goTo(this.activeIndex + 1);
    }
  }

  start() {
    if (!this.autoplayEnabled || !this.isMobile() || this.pages.length < 2) return;
    this.stop();
    this.timer = window.setInterval(() => {
      this.goTo(this.activeIndex + 1, false);
    }, this.interval);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  goTo(index, restart = true) {
    if (!this.track || !this.pages.length) return;

    let nextIndex = index;
    if (this.loopEnabled) {
      nextIndex = (index + this.pages.length) % this.pages.length;
    } else {
      nextIndex = Math.max(0, Math.min(index, this.pages.length - 1));
    }

    this.activeIndex = nextIndex;
    this.track.style.transform = `translate3d(-${this.activeIndex * 100}%, 0, 0)`;

    this.pages.forEach((page, pageIndex) => {
      page.classList.toggle('is-active', pageIndex === this.activeIndex);
    });

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    if (restart) {
      this.start();
    }
  }

  goToBlock(blockId) {
    const index = this.pages.findIndex((page) => {
      const ids = page.dataset.pageBlockIds || '';
      return ids.split(',').includes(blockId);
    });
    if (index >= 0) {
      this.goTo(index, false);
      this.stop();
    }
  }
}

const categoryShowcaseControllers = new WeakMap();

function initCategoryShowcases(container = document) {
  container.querySelectorAll?.('[data-category-showcase]').forEach((root) => {
    if (categoryShowcaseControllers.has(root)) return;
    categoryShowcaseControllers.set(root, new CategoryShowcaseController(root));
  });
}

function destroyCategoryShowcases(container = document) {
  container.querySelectorAll?.('[data-category-showcase]').forEach((root) => {
    const controller = categoryShowcaseControllers.get(root);
    if (!controller) return;
    controller.destroy();
    categoryShowcaseControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCategoryShowcases(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initCategoryShowcases(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyCategoryShowcases(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  const blockNode = event.target.closest?.('[data-category-block-id]');
  const root = event.target.closest?.('[data-category-showcase]');
  if (!blockNode || !root) return;
  const controller = categoryShowcaseControllers.get(root);
  controller?.goToBlock(blockNode.dataset.categoryBlockId);
});

class CollectionShowcaseController {
  constructor(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-collection-showcase-viewport]');
    this.prevButton = root.querySelector('[data-collection-showcase-prev]');
    this.nextButton = root.querySelector('[data-collection-showcase-next]');
    this.pagination = root.querySelector('[data-collection-showcase-pagination]');
    this.dots = [];
    this.pageCount = 1;

    this.handlePrev = this.handlePrev.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    if (!this.viewport) return;

    this.attachEvents();
    this.rebuild();
  }

  attachEvents() {
    this.prevButton?.addEventListener('click', this.handlePrev);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
  }

  destroy() {
    this.prevButton?.removeEventListener('click', this.handlePrev);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

  getPageWidth() {
    return this.viewport?.clientWidth || 1;
  }

  getActivePage() {
    if (!this.viewport) return 0;
    return Math.round(this.viewport.scrollLeft / this.getPageWidth());
  }

  rebuildDots() {
    if (!this.pagination) return;
    this.pagination.innerHTML = '';
    this.dots = [];

    if (this.pageCount <= 1) {
      this.pagination.hidden = true;
      return;
    }

    this.pagination.hidden = false;

    for (let index = 0; index < this.pageCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'collection-showcase__dot';
      dot.setAttribute('aria-label', `Go to products page ${index + 1}`);
      dot.addEventListener('click', () => {
        this.scrollToPage(index);
      });
      this.pagination.appendChild(dot);
      this.dots.push(dot);
    }
  }

  updateControls() {
    const activePage = this.getActivePage();

    this.prevButton && (this.prevButton.disabled = activePage <= 0);
    this.nextButton && (this.nextButton.disabled = activePage >= this.pageCount - 1);

    this.dots.forEach((dot, index) => {
      const isActive = index === activePage;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  rebuild() {
    if (!this.viewport) return;

    const scrollable = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    this.pageCount = scrollable > 4 ? Math.ceil(scrollable / this.getPageWidth()) + 1 : 1;
    this.rebuildDots();
    this.updateControls();
  }

  scrollToPage(index) {
    if (!this.viewport) return;
    const clampedIndex = Math.max(0, Math.min(index, this.pageCount - 1));
    this.viewport.scrollTo({
      left: clampedIndex * this.getPageWidth(),
      behavior: 'smooth',
    });
  }

  handlePrev() {
    this.scrollToPage(this.getActivePage() - 1);
  }

  handleNext() {
    this.scrollToPage(this.getActivePage() + 1);
  }

  handleResize() {
    this.rebuild();
  }

  handleScroll() {
    this.updateControls();
  }
}

const collectionShowcaseControllers = new WeakMap();

function initCollectionShowcases(container = document) {
  container.querySelectorAll?.('[data-collection-showcase]').forEach((root) => {
    if (collectionShowcaseControllers.has(root)) return;
    collectionShowcaseControllers.set(root, new CollectionShowcaseController(root));
  });
}

function destroyCollectionShowcases(container = document) {
  container.querySelectorAll?.('[data-collection-showcase]').forEach((root) => {
    const controller = collectionShowcaseControllers.get(root);
    if (!controller) return;
    controller.destroy();
    collectionShowcaseControllers.delete(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initCollectionShowcases(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initCollectionShowcases(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyCollectionShowcases(event.target);
});
